<div class="add-on-override-section">
  @for (row of rows(); track row.key) {
    <mat-card class="add-on-card">
      <mat-card-header class="flex gap-2">
        <mat-card-title>{{ row.label }}</mat-card-title>
        <pr-pill
          class="status-badge status-{{ row.addOn.billingStatus }}"
          [attr.aria-label]="'Stripe status: ' + row.billingLabel"
          >{{ row.billingLabel }}</pr-pill
        >

        @if (row.addOn.override) {
          @if (row.isExpired) {
            <pr-pill
              class="override-badge override-expired"
              aria-label="Override state: Expired (Auto)"
            >
              Expired (Auto)
            </pr-pill>
          } @else {
            <pr-pill
              class="override-badge override-active"
              [attr.aria-label]="'Override state: ' + row.overrideLabel"
            >
              {{ row.overrideLabel }}
            </pr-pill>
          }
        }

        <pr-pill
          class="resolved-badge resolved-{{ row.isEnabled ? 'yes' : 'no' }}"
          [attr.aria-label]="'Enabled: ' + (row.isEnabled ? 'YES' : 'NO')"
        >
          Enabled: {{ row.isEnabled ? 'YES' : 'NO' }}
        </pr-pill>
      </mat-card-header>
      <mat-card-content>
        @if (isManagementUser()) {
          <form class="flex flex-col gap-4 pt-4" [formGroup]="row.form">
            <mat-form-field>
              <mat-label>Override State</mat-label>
              <mat-select formControlName="state">
                <mat-option [value]="overrideResolution.Auto">
                  {{ overrideLabels[overrideResolution.Auto] }} (Stripe-derived)
                </mat-option>
                <mat-option [value]="overrideResolution.ForceEnabled">
                  {{ overrideLabels[overrideResolution.ForceEnabled] }}
                </mat-option>
                <mat-option [value]="overrideResolution.ForceDisabled">
                  {{ overrideLabels[overrideResolution.ForceDisabled] }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            @if (row.form.value.state !== overrideResolution.Auto) {
              <mat-form-field>
                <mat-label>Override reason</mat-label>
                <textarea
                  matInput
                  rows="2"
                  placeholder="Operational reason for this override (min 10 characters)"
                  formControlName="reason"
                ></textarea>
              </mat-form-field>
              @if (row.form.controls.reason.invalid) {
                <p class="override-error save-reason-error text-red-700">
                  Reason is required (min 10 characters)
                </p>
              }

              <pt-iso-date-selector
                formControlName="expiresAt"
                placeholder="No expiry"
                [min]="minExpiryDate"
              />
              @if (row.form.controls.expiresAt.invalid) {
                <p class="override-error expiry-past-error text-red-700">
                  Expiry must be in the future
                </p>
              }

              @if (row.form.value.state === overrideResolution.ForceDisabled) {
                <mat-checkbox formControlName="forceDisabledConfirmed">
                  Confirm Force disabled will block access to this add-on.
                </mat-checkbox>
              }
            }

            <div class="flex flex-col">
              @if (row.addOn.override) {
                <p class="override-meta">
                  Set at:
                  {{
                    row.addOn.override.setAt
                      | moment
                      | amDateFormat: dateTimeFormat
                  }}
                </p>
                @if (row.setByDisplay$ | async; as setByDisplay) {
                  <p class="override-meta">Set by: {{ setByDisplay }}</p>
                }
                @if (row.addOn.override.expiresAt) {
                  <p class="override-meta">
                    Expires:
                    {{
                      row.addOn.override.expiresAt
                        | moment
                        | amDateFormat: dateFormat
                    }}
                  </p>
                }
                @if (row.addOn.override.reason) {
                  <p class="override-reason">
                    Reason: {{ row.addOn.override.reason }}
                  </p>
                }
              }

              @if (row.error(); as error) {
                <p
                  class="override-error text-red-700"
                  role="alert"
                  aria-live="polite"
                  [id]="row.errorId"
                >
                  {{ error }}
                </p>
              }
            </div>

            @if (row.clearMode()) {
              <mat-form-field>
                <mat-label>Reason for clearing override</mat-label>
                <textarea
                  matInput
                  rows="2"
                  placeholder="Operational reason for clearing this override (min 10 characters)"
                  formControlName="clearReason"
                ></textarea>
              </mat-form-field>
              @if (row.form.controls.clearReason.invalid) {
                <p class="override-error clear-reason-error text-red-700">
                  Reason is required (min 10 characters)
                </p>
              }
              <pt-buttons-container padding="none">
                <button
                  mat-flat-button
                  color="warn"
                  type="button"
                  [disabled]="
                    row.saving() || row.form.controls.clearReason.invalid
                  "
                  (click)="confirmClearOverride(row)"
                >
                  Confirm Clear
                </button>
                <button
                  mat-stroked-button
                  type="button"
                  [disabled]="row.saving()"
                  (click)="cancelClearMode(row)"
                >
                  Cancel
                </button>
              </pt-buttons-container>
            } @else {
              <pt-buttons-container padding="none">
                @if (
                  row.addOn.override ||
                  row.form.value.state !== overrideResolution.Auto
                ) {
                  <button
                    mat-flat-button
                    color="primary"
                    type="button"
                    [disabled]="row.saving() || row.form.invalid"
                    [attr.aria-describedby]="
                      row.error() ? row.errorId : undefined
                    "
                    (click)="saveOverride(row)"
                  >
                    Save Override
                  </button>
                }
                @if (row.addOn.override) {
                  <button
                    mat-stroked-button
                    color="warn"
                    type="button"
                    [disabled]="row.saving()"
                    (click)="enterClearMode(row)"
                  >
                    Clear Override
                  </button>
                }
              </pt-buttons-container>
            }
          </form>
        } @else {
          <div class="flex flex-col pt-4">
            @if (row.addOn.override) {
              <p class="override-meta">
                Set at:
                {{
                  row.addOn.override.setAt
                    | moment
                    | amDateFormat: dateTimeFormat
                }}
              </p>
              @if (row.setByDisplay$ | async; as setByDisplay) {
                <p class="override-meta">Set by: {{ setByDisplay }}</p>
              }
              @if (row.addOn.override.expiresAt) {
                <p class="override-meta">
                  Expires:
                  {{
                    row.addOn.override.expiresAt
                      | moment
                      | amDateFormat: dateFormat
                  }}
                </p>
              }
              @if (row.addOn.override.reason) {
                <p class="override-reason">
                  Reason: {{ row.addOn.override.reason }}
                </p>
              }
            }
          </div>
        }
      </mat-card-content>
    </mat-card>
  } @empty {
    <p class="no-products">No add-ons found.</p>
  }
</div>
